import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Pencil, Plus, Trash2, Cable, Loader2 } from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TRANSPORTS = ["udp", "tcp", "tls"];
const DTMF = ["rfc2833", "inband"];
const AUTH_TYPES = [
  { v: "ip", label: "IP-based (no credentials)" },
  { v: "userpass", label: "Username + password" },
];

const blankTrunk = {
  name: "",
  auth_type: "ip",
  host: "",
  port: 5060,
  transport: "udp",
  allowed_cidrs: "",
  username: "",
  password: "",
  sip_register: false,
  codec_preference: "pcmu,pcma,opus",
  dtmf_mode: "rfc2833",
  max_concurrent_calls: 30,
  enabled: true,
};

function toCsv(arr) {
  return Array.isArray(arr) ? arr.join(",") : arr || "";
}
function fromCsv(s) {
  return (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function Trunks() {
  const [trunks, setTrunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {id, ...} = edit
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get("/trunks");
      setTrunks(res.data);
    } catch (err) {
      toastError(err, "Failed to load trunks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openCreate = () => setEditing({ ...blankTrunk });
  const openEdit = (t) =>
    setEditing({
      ...blankTrunk,
      ...t,
      password: "", // never prefill password
      allowed_cidrs: toCsv(t.allowed_cidrs),
      codec_preference: toCsv(t.codec_preference),
    });

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/trunks/${confirmDelete.id}`);
      toast.success(`Deleted "${confirmDelete.name}"`);
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toastError(err, "Failed to delete trunk");
    }
  };

  return (
    <div className="space-y-8" data-testid="trunks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">SIP Trunks</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Define the SIP trunks the voice worker uses to send and receive calls.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="trunks-add-btn">
          <Plus className="h-4 w-4" /> Add trunk
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading trunks…
          </div>
        ) : trunks.length === 0 ? (
          <EmptyState onAdd={openCreate} />
        ) : (
          <Table data-testid="trunks-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Auth</TableHead>
                <TableHead>Host:Port</TableHead>
                <TableHead>Concurrency</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trunks.map((t) => (
                <TableRow key={t.id} data-testid={`trunks-row-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase text-[10px]">
                      {t.auth_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {t.host}:{t.port} <span className="text-muted-foreground">/{t.transport}</span>
                  </TableCell>
                  <TableCell>{t.max_concurrent_calls}</TableCell>
                  <TableCell>
                    {t.enabled ? (
                      <Badge>Enabled</Badge>
                    ) : (
                      <Badge variant="secondary">Disabled</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(t)}
                      data-testid={`trunks-edit-${t.id}`}
                    >
                      <Pencil className="h-4 w-4" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDelete(t)}
                      data-testid={`trunks-delete-${t.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" /> Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="trunks-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit trunk" : "Add trunk"}</DialogTitle>
            <DialogDescription>
              Configure SIP signalling, authentication and capacity for this trunk.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <TrunkForm
              key={editing.id || "new"}
              initial={editing}
              submitting={submitting}
              onCancel={() => setEditing(null)}
              onSubmit={async (payload) => {
                setSubmitting(true);
                try {
                  if (editing.id) {
                    // PUT — strip server-controlled fields
                    const { id, has_password, created_at, updated_at, ...rest } =
                      payload;
                    if (rest.password === "") rest.password = ""; // explicit clear
                    if (!rest.password) delete rest.password; // unchanged
                    await api.put(`/trunks/${id}`, rest);
                    toast.success("Trunk updated");
                  } else {
                    await api.post("/trunks", payload);
                    toast.success("Trunk created");
                  }
                  setEditing(null);
                  fetchAll();
                } catch (err) {
                  toastError(err, "Failed to save trunk");
                } finally {
                  setSubmitting(false);
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="trunks-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete trunk?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{confirmDelete?.name}</b>. Calls
              currently routed through it will fail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="trunks-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div
      className="flex flex-col items-center gap-3 py-16 text-center"
      data-testid="trunks-empty"
    >
      <div className="rounded-full bg-secondary p-3">
        <Cable className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="text-base font-medium">No trunks yet</div>
      <p className="max-w-sm text-sm text-muted-foreground">
        Add your first SIP trunk to connect VoxIVR to your carrier or PBX.
        You'll need either an authorised IP range or username + password
        credentials from the provider.
      </p>
      <Button onClick={onAdd} className="mt-2" data-testid="trunks-empty-add">
        <Plus className="h-4 w-4" /> Add trunk
      </Button>
    </div>
  );
}

function TrunkForm({ initial, submitting, onCancel, onSubmit }) {
  const {
    register: rhf,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: initial });

  const authType = watch("auth_type");

  const submit = (v) => {
    const payload = {
      ...v,
      port: Number(v.port),
      max_concurrent_calls: Number(v.max_concurrent_calls),
      allowed_cidrs: fromCsv(v.allowed_cidrs),
      codec_preference: fromCsv(v.codec_preference),
    };
    if (authType === "ip") {
      payload.username = null;
      // password handled by parent
    }
    onSubmit(payload);
  };

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="grid grid-cols-2 gap-4"
      data-testid="trunk-form"
    >
      <div className="col-span-2 space-y-1.5">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          data-testid="trunk-name"
          {...rhf("name", { required: "Name is required" })}
        />
        {errors.name && (
          <p className="text-xs text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Auth type</Label>
        <Select
          value={watch("auth_type")}
          onValueChange={(v) => setValue("auth_type", v)}
        >
          <SelectTrigger data-testid="trunk-auth-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUTH_TYPES.map((a) => (
              <SelectItem key={a.v} value={a.v}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>Transport</Label>
        <Select
          value={watch("transport")}
          onValueChange={(v) => setValue("transport", v)}
        >
          <SelectTrigger data-testid="trunk-transport">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRANSPORTS.map((t) => (
              <SelectItem key={t} value={t}>
                {t.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="host">Host</Label>
        <Input
          id="host"
          data-testid="trunk-host"
          placeholder="sip.carrier.com"
          {...rhf("host", { required: "Host is required" })}
        />
        {errors.host && (
          <p className="text-xs text-destructive">{errors.host.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="port">Port</Label>
        <Input
          id="port"
          type="number"
          min={1}
          max={65535}
          data-testid="trunk-port"
          {...rhf("port", { required: true, valueAsNumber: true })}
        />
      </div>

      {authType === "userpass" ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="username">SIP username</Label>
            <Input
              id="username"
              autoComplete="off"
              data-testid="trunk-username"
              {...rhf("username", {
                required:
                  authType === "userpass" ? "Username required" : false,
              })}
            />
            {errors.username && (
              <p className="text-xs text-destructive">
                {errors.username.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">
              SIP password
              {initial.has_password && (
                <span className="ml-2 text-xs text-muted-foreground">
                  (leave blank to keep existing)
                </span>
              )}
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              data-testid="trunk-password"
              {...rhf("password")}
            />
          </div>
        </>
      ) : (
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor="allowed_cidrs">
            Allowed CIDRs
            <span className="ml-2 text-xs text-muted-foreground">
              comma-separated (e.g. 1.2.3.4/32, 10.0.0.0/24)
            </span>
          </Label>
          <Input
            id="allowed_cidrs"
            data-testid="trunk-cidrs"
            {...rhf("allowed_cidrs")}
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label>DTMF mode</Label>
        <Select
          value={watch("dtmf_mode")}
          onValueChange={(v) => setValue("dtmf_mode", v)}
        >
          <SelectTrigger data-testid="trunk-dtmf">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DTMF.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="max_concurrent_calls">Max concurrent calls</Label>
        <Input
          id="max_concurrent_calls"
          type="number"
          min={1}
          max={10000}
          data-testid="trunk-concurrency"
          {...rhf("max_concurrent_calls", { valueAsNumber: true })}
        />
      </div>

      <div className="col-span-2 space-y-1.5">
        <Label htmlFor="codec_preference">
          Codec preference{" "}
          <span className="text-xs text-muted-foreground">
            comma-separated, in priority order
          </span>
        </Label>
        <Input
          id="codec_preference"
          data-testid="trunk-codecs"
          {...rhf("codec_preference")}
        />
      </div>

      <div className="col-span-2 flex items-center justify-between rounded-md border border-border p-3">
        <div className="flex items-center gap-3">
          <Switch
            checked={!!watch("enabled")}
            onCheckedChange={(v) => setValue("enabled", v)}
            data-testid="trunk-enabled"
          />
          <Label className="cursor-pointer">Enabled</Label>
        </div>
        {authType === "userpass" && (
          <div className="flex items-center gap-3">
            <Switch
              checked={!!watch("sip_register")}
              onCheckedChange={(v) => setValue("sip_register", v)}
              data-testid="trunk-register"
            />
            <Label className="cursor-pointer">Register with carrier</Label>
          </div>
        )}
      </div>

      <DialogFooter className="col-span-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting} data-testid="trunk-submit">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {initial.id ? "Save changes" : "Create trunk"}
        </Button>
      </DialogFooter>
    </form>
  );
}
